import type { BlockDef } from "./schema/block.ts";
import type { UiElement } from "./schema/element.ts";

/**
 * RenderLane is the app-level build/runtime capability vocabulary.
 *
 * Keep this distinct from ontology-local lane labels such as "pixi" and "r3f".
 * The ontology may describe a pattern with cogor:lane "pixi"; an application
 * manifest should declare the deploy/runtime requirement as "game-2d".
 */
export const RENDER_LANES = [
  "core-dom",
  "forms",
  "overlay",
  "navigation",
  "data-table",
  "data-viz",
  "calendar",
  "command",
  "carousel",
  "lottie",
  "media",
  "game-2d",
  "game-3d",
] as const;

export type RenderLane = (typeof RENDER_LANES)[number];

export type RenderProfileSource =
  | "ui-scan"
  | "ontology"
  | "kg-retrieval"
  | "generator"
  | "manual"
  | "migration";

export interface RenderProfile {
  version: "render-profile.v1";
  /**
   * Closed, sorted, duplicate-free render lanes required by this app.
   * Apps with no heavy/specialized elements should still declare ["core-dom"].
   */
  lanes: RenderLane[];
  /** Concrete island registry keys found in the app UI tree. */
  islandNames?: string[];
  /** Concrete Cogo element types observed while deriving the profile. */
  elementTypes?: string[];
  /** Provenance of the profile; this is evidence, not behavior. */
  sources?: RenderProfileSource[];
  /** Optional human-readable notes for handoffs or migration records. */
  notes?: string[];
}

const RENDER_LANE_SET = new Set<string>(RENDER_LANES);

const ONTOLOGY_LANE_ALIASES: Record<string, RenderLane> = {
  dom: "core-dom",
  pixi: "game-2d",
  r3f: "game-3d",
  chart: "data-viz",
  "data-grid": "data-table",
};

const ELEMENT_TYPE_TO_LANES: Record<string, readonly RenderLane[]> = {
  form: ["forms"],
  input: ["forms"],
  textarea: ["forms"],
  checkbox: ["forms"],
  radio: ["forms"],
  switch: ["forms"],
  slider: ["forms"],
  select: ["forms"],
  "native-select": ["forms"],
  "input-otp": ["forms"],
  "chip-input": ["forms"],
  "phone-input": ["forms"],

  dialog: ["overlay"],
  "alert-dialog": ["overlay"],
  sheet: ["overlay"],
  drawer: ["overlay"],
  popover: ["overlay"],
  "dropdown-menu": ["overlay"],
  "context-menu": ["overlay"],
  tooltip: ["overlay"],
  toast: ["overlay"],
  "hover-card": ["overlay"],

  breadcrumb: ["navigation"],
  pagination: ["navigation"],
  "navigation-menu": ["navigation"],
  sidebar: ["navigation"],
  "sidebar-inset": ["navigation"],
  "sidebar-trigger": ["navigation"],
  link: ["navigation"],

  table: ["data-table"],
  "data-table": ["data-table"],
  data_table: ["data-table"],

  chart: ["data-viz"],
  "ring-progress": ["data-viz"],
  waveform: ["data-viz"],

  calendar: ["calendar"],
  "date-picker": ["calendar"],
  "month-picker": ["calendar"],

  command: ["command"],
  carousel: ["carousel"],
  lottie: ["lottie"],
  video: ["media"],
  iframe: ["media"],
};

const ISLAND_NAME_TO_LANES: Record<string, readonly RenderLane[]> = {
  "cogor.game.pixi-stage": ["game-2d"],
  "cogor.game.r3f-scene": ["game-3d"],
};

export function normalizeRenderLane(value: string): RenderLane | null {
  const lane = value.trim();
  if (RENDER_LANE_SET.has(lane)) return lane as RenderLane;
  return ONTOLOGY_LANE_ALIASES[lane] ?? null;
}

export function inferRenderLanesForElement(element: Pick<UiElement, "type" | "island">): RenderLane[] {
  const lanes = new Set<RenderLane>(["core-dom"]);
  for (const lane of ELEMENT_TYPE_TO_LANES[element.type] ?? []) {
    lanes.add(lane);
  }
  if (element.type === "island" && element.island) {
    for (const lane of ISLAND_NAME_TO_LANES[element.island] ?? []) {
      lanes.add(lane);
    }
  }
  return sortRenderLanes([...lanes]);
}

export function deriveRenderProfileFromBlocks(
  blocks: Record<string, BlockDef>,
  sources: RenderProfileSource[] = ["ui-scan"],
): RenderProfile {
  const lanes = new Set<RenderLane>(["core-dom"]);
  const elementTypes = new Set<string>();
  const islandNames = new Set<string>();

  const visit = (element: UiElement | undefined): void => {
    if (!element) return;
    elementTypes.add(element.type);
    for (const lane of inferRenderLanesForElement(element)) {
      lanes.add(lane);
    }
    if (element.type === "island" && element.island) {
      islandNames.add(element.island);
    }
    for (const child of element.children ?? []) {
      visit(child);
    }
  };

  for (const block of Object.values(blocks)) {
    visit(block.ui?.root);
  }

  return {
    version: "render-profile.v1",
    lanes: sortRenderLanes([...lanes]),
    ...(islandNames.size > 0 ? { islandNames: [...islandNames].sort() } : {}),
    ...(elementTypes.size > 0 ? { elementTypes: [...elementTypes].sort() } : {}),
    sources: [...new Set(sources)].sort(),
  };
}

function sortRenderLanes(lanes: RenderLane[]): RenderLane[] {
  return [...new Set(lanes)].sort((a, b) => RENDER_LANES.indexOf(a) - RENDER_LANES.indexOf(b));
}
